import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Search, Users, Phone, MapPin, Loader2, Edit, Trash2, Eye, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { PhoneWithWhatsApp } from '../components/WhatsAppLink';

const PRIORITA = [
  { value: 'bassa', label: 'Bassa' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const FamigliaForm = ({ famiglia, chiese, isSuperAdmin, onSave, onClose }) => {
  const [formData, setFormData] = useState(famiglia || {
    nome_referente: '', codice_famiglia: '', telefono: '', email: '',
    indirizzo: '', comune: '', cap: '', chiesa_id: '',
    numero_adulti: 1, bambini_0_3: 0, bambini_4_5: 0, bambini_6_12: 0,
    ragazzi_13_17: 0, anziani: 0, disabili: 0,
    isee: 0, scadenza_isee: '', priorita_sociale: 'media',
    stato_pratica: 'attivo', note_operatore: '',
    famiglia_monogenitoriale: false, disoccupazione: false,
    emergenza_sociale: false, presenza_neonati: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const processedData = {
        ...formData,
        numero_adulti: parseInt(formData.numero_adulti) || 0,
        bambini_0_3: parseInt(formData.bambini_0_3) || 0,
        bambini_4_5: parseInt(formData.bambini_4_5) || 0,
        bambini_6_12: parseInt(formData.bambini_6_12) || 0,
        ragazzi_13_17: parseInt(formData.ragazzi_13_17) || 0,
        anziani: parseInt(formData.anziani) || 0,
        disabili: parseInt(formData.disabili) || 0,
        isee: parseFloat(formData.isee) || 0,
        email: formData.email || null,
        telefono: formData.telefono || null,
        indirizzo: formData.indirizzo || null,
        comune: formData.comune || null,
        cap: formData.cap || null,
        scadenza_isee: formData.scadenza_isee || null,
        note_operatore: formData.note_operatore || null,
      };
      await onSave(processedData);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
      {/* Selezione Chiesa (solo super admin) */}
      {isSuperAdmin && !famiglia && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Assegna a Chiesa</p>
          <div className="space-y-1.5">
            <Label>Chiesa *</Label>
            <Select value={formData.chiesa_id} onValueChange={(v) => updateField('chiesa_id', v)}>
              <SelectTrigger data-testid="famiglia-chiesa-select"><SelectValue placeholder="Seleziona chiesa" /></SelectTrigger>
              <SelectContent>
                {(chiese || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Sezione Anagrafica */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Anagrafica</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Nome Referente *</Label>
            <Input value={formData.nome_referente} onChange={(e) => updateField('nome_referente', e.target.value)} required data-testid="famiglia-nome-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Codice Famiglia</Label>
            <Input value={formData.codice_famiglia} onChange={(e) => updateField('codice_famiglia', e.target.value)} placeholder="Auto-generato" data-testid="famiglia-codice-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Telefono</Label>
            <Input value={formData.telefono} onChange={(e) => updateField('telefono', e.target.value)} type="tel" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={formData.email} onChange={(e) => updateField('email', e.target.value)} type="email" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Indirizzo</Label>
            <Input value={formData.indirizzo} onChange={(e) => updateField('indirizzo', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Comune</Label>
              <Input value={formData.comune} onChange={(e) => updateField('comune', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Sezione Composizione Nucleo */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Composizione Nucleo</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: 'numero_adulti', label: 'Adulti' },
            { key: 'bambini_0_3', label: '0-3 anni' },
            { key: 'bambini_4_5', label: '4-5 anni' },
            { key: 'bambini_6_12', label: '6-12 anni' },
            { key: 'ragazzi_13_17', label: '13-17 anni' },
            { key: 'anziani', label: 'Anziani' },
            { key: 'disabili', label: 'Disabili' },
          ].map(({ key, label }) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input type="number" min="0" value={formData[key]} onChange={(e) => updateField(key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* Sezione Situazione */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Situazione</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>ISEE</Label>
            <Input type="number" step="0.01" min="0" value={formData.isee} onChange={(e) => updateField('isee', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Scadenza ISEE</Label>
            <Input type="date" value={formData.scadenza_isee} onChange={(e) => updateField('scadenza_isee', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Priorita</Label>
            <Select value={formData.priorita_sociale} onValueChange={(v) => updateField('priorita_sociale', v)}>
              <SelectTrigger data-testid="famiglia-priorita-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITA.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 sm:gap-6 pt-1">
          {[
            { key: 'famiglia_monogenitoriale', label: 'Monogenitoriale' },
            { key: 'disoccupazione', label: 'Disoccupazione' },
            { key: 'emergenza_sociale', label: 'Emergenza' },
            { key: 'presenza_neonati', label: 'Neonati' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={formData[key]} onCheckedChange={(v) => updateField(key, v)} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Note Operatore</Label>
        <Textarea value={formData.note_operatore} onChange={(e) => updateField('note_operatore', e.target.value)} rows={2} />
      </div>

      <DialogFooter className="flex-col sm:flex-row gap-2 pt-2 sticky bottom-0 bg-background pb-1">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="famiglia-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {famiglia ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Mobile card for a single family
const FamigliaMobileCard = ({ f, navigate, isAdmin, openEdit, handleDelete }) => {
  const prioritaColor = {
    alta: 'destructive', urgente: 'destructive', media: 'secondary', bassa: 'outline'
  };
  return (
    <div className="mobile-card-item" data-testid={`famiglia-card-${f.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0" onClick={() => navigate(`/famiglie/${f.id}`)} role="button">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{f.nome_referente}</span>
            <Badge variant={prioritaColor[f.priorita_sociale] || 'outline'} className="text-[10px] px-1.5 py-0">
              {f.priorita_sociale}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{f.totale_componenti}</span>
            {f.comune && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{f.comune}</span>}
          </div>
          {f.telefono && (
            <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
              <PhoneWithWhatsApp phone={f.telefono} message={`Ciao ${f.nome_referente}, ti contattiamo dal banco alimentare.`} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAdmin() && (
            <>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEdit(f)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => handleDelete(f.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(`/famiglie/${f.id}`)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const FamigliePage = () => {
  const { API, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [famiglie, setFamiglie] = useState([]);
  const [chiese, setChiese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFamiglia, setEditingFamiglia] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const promises = [axios.get(`${API}/famiglie`)];
      if (isSuperAdmin()) promises.push(axios.get(`${API}/chiese`));
      const results = await Promise.all(promises);
      setFamiglie(results[0].data);
      if (results[1]) setChiese(results[1].data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (editingFamiglia) {
      await axios.put(`${API}/famiglie/${editingFamiglia.id}`, data);
      toast.success('Famiglia aggiornata');
    } else {
      await axios.post(`${API}/famiglie`, data);
      toast.success('Famiglia creata');
    }
    fetchData();
    setEditingFamiglia(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa famiglia?')) return;
    try {
      await axios.delete(`${API}/famiglie/${id}`);
      toast.success('Famiglia eliminata');
      fetchData();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const openEdit = (f) => { setEditingFamiglia(f); setDialogOpen(true); };
  const openCreate = () => { setEditingFamiglia(null); setDialogOpen(true); };

  const filteredFamiglie = famiglie.filter(f =>
    f.nome_referente.toLowerCase().includes(search.toLowerCase()) ||
    (f.codice_famiglia || '').toLowerCase().includes(search.toLowerCase())
  );

  const prioritaColor = {
    alta: 'destructive', urgente: 'destructive', media: 'secondary', bassa: 'outline'
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="famiglie-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Famiglie</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Gestisci le famiglie assistite dal banco alimentare</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button onClick={openCreate} className="w-full sm:w-auto" data-testid="add-famiglia-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Famiglia
            </Button>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingFamiglia ? 'Modifica Famiglia' : 'Nuova Famiglia'}</DialogTitle>
              </DialogHeader>
              <FamigliaForm famiglia={editingFamiglia} chiese={chiese} isSuperAdmin={isSuperAdmin()} onSave={handleSave} onClose={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Cerca famiglia..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="famiglie-search-input"
        />
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filteredFamiglie.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">Nessuna famiglia trovata</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referente</TableHead>
                      <TableHead>Codice</TableHead>
                      <TableHead className="text-center">Componenti</TableHead>
                      <TableHead>Contatti</TableHead>
                      <TableHead className="hidden lg:table-cell">Comune</TableHead>
                      <TableHead>Priorita</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFamiglie.map((f) => (
                      <TableRow key={f.id} className="cursor-pointer hover:bg-muted/50" data-testid={`famiglia-row-${f.id}`}>
                        <TableCell onClick={() => navigate(`/famiglie/${f.id}`)}>
                          <span className="font-medium">{f.nome_referente}</span>
                        </TableCell>
                        <TableCell onClick={() => navigate(`/famiglie/${f.id}`)}>
                          <span className="font-mono text-xs text-muted-foreground">{f.codice_famiglia}</span>
                        </TableCell>
                        <TableCell className="text-center" onClick={() => navigate(`/famiglie/${f.id}`)}>
                          <div className="flex items-center justify-center gap-1">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{f.totale_componenti}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {f.telefono && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              <PhoneWithWhatsApp phone={f.telefono} message={`Ciao ${f.nome_referente}, ti contattiamo dal banco alimentare.`} />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell" onClick={() => navigate(`/famiglie/${f.id}`)}>
                          {f.comune || '-'}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/famiglie/${f.id}`)}>
                          <Badge variant={prioritaColor[f.priorita_sociale] || 'outline'}>
                            {f.priorita_sociale}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isAdmin() && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-famiglia-${f.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(f.id)} className="text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/famiglie/${f.id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card List */}
              <div className="md:hidden">
                {filteredFamiglie.map((f) => (
                  <FamigliaMobileCard
                    key={f.id}
                    f={f}
                    navigate={navigate}
                    isAdmin={isAdmin}
                    openEdit={openEdit}
                    handleDelete={handleDelete}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FamigliePage;
